import { getAnalyticsWebClient } from '@confluence/analytics-web-client';

const ErrorType = Object.freeze({
	REMOVE_CHILD_ERROR: 'removeChildError',
	INSERT_BEFORE_ERROR: 'insertBeforeError',
});

let patchesApplied = false;

/**
 * Applies the Node.prototype monkey-patches that suppress DOM errors
 * caused by Google Translate moving nodes between parents.
 * This is idempotent — calling it multiple times has no additional effect.
 */
export function applyDOMPatches() {
	if (patchesApplied) return;
	patchesApplied = true;

	if (typeof Node === 'function' && Node.prototype) {
		const originalRemoveChild = Node.prototype.removeChild;
		Node.prototype.removeChild = function (child) {
			if (child.parentNode !== this) {
				if (console) {
					console.error('Cannot remove a child from a different parent', child, this);

					sendRemoveChildErrorAnalyticsEvent(child);
				}
				return child;
			}
			return originalRemoveChild.apply(this, arguments);
		};

		const originalInsertBefore = Node.prototype.insertBefore;
		Node.prototype.insertBefore = function (newNode, referenceNode) {
			if (referenceNode && referenceNode.parentNode !== this) {
				if (console) {
					console.error(
						'Cannot insert before a reference node from a different parent',
						referenceNode,
						this,
					);

					sendInsertBeforeErrorAnalyticsEvent(referenceNode, newNode);
				}
				return newNode;
			}
			return originalInsertBefore.apply(this, arguments);
		};
	}
}

/**
 * Checks whether Google Translate is currently active on the page.
 * Google Translate adds a "translated-*" class to <html> when active.
 */
function isGoogleTranslateActive() {
	const htmlEl = document.documentElement;
	return htmlEl && htmlEl.classList && htmlEl.className.match(/translated/i);
}

/**
 * Lazily handles DOM issues caused by Google Translate.
 *
 * Instead of unconditionally monkey-patching Node.prototype.removeChild and
 * Node.prototype.insertBefore for all users (adding overhead to every DOM operation),
 * this function:
 *   1. Checks if Google Translate is already active and applies patches immediately if so.
 *   2. Otherwise, sets up a lightweight MutationObserver on <html> to watch for the
 *      "translated" class that Google Translate adds, and only applies the patches
 *      when translation is detected.
 *
 * This avoids adding extra if-checks to every removeChild/insertBefore call for the
 * vast majority of users who don't use Google Translate.
 *
 * @see https://github.com/facebook/react/issues/11538#issuecomment-417504600
 */
export function handleDOMGoogleTranslateIssues() {
	// If Google Translate is already active (e.g. auto-translate on page load), patch immediately
	if (isGoogleTranslateActive()) {
		applyDOMPatches();
		return;
	}

	// Watch for Google Translate activation via class changes on <html>
	if (typeof MutationObserver !== 'undefined' && document.documentElement) {
		const observer = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (
					mutation.type === 'attributes' &&
					mutation.attributeName === 'class' &&
					isGoogleTranslateActive()
				) {
					applyDOMPatches();
					observer.disconnect();
					return;
				}
			}
		});

		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['class'],
		});
	} else {
		// Fallback: if MutationObserver is not available, apply patches unconditionally
		applyDOMPatches();
	}
}

/**
 * Send an analytics event when a 'removeChild' error occurs. This function helps to track the occurrence of this suppressed error.
 * @param {Node} childNode - The node that is attempted to be removed.
 */
function sendRemoveChildErrorAnalyticsEvent(childNode) {
	const nodeData = {
		nodeToBeRemoved: extractNodeData(childNode),
	};
	sendAnalyticsEvent(ErrorType.REMOVE_CHILD_ERROR, nodeData);
}

/**
 * Send an analytics event when an 'insertBefore' error occurs.  This function helps to track the occurrence of this suppressed error.
 * @param {Node} referenceNode - The existing node in the DOM tree. The new node is intended to be inserted before this node.
 * @param {Node} newNode - The new node that is attempted to be inserted.
 */
function sendInsertBeforeErrorAnalyticsEvent(referenceNode, newNode) {
	const nodeData = {
		newNode: extractNodeData(newNode),
		referenceNode: extractNodeData(referenceNode),
	};
	sendAnalyticsEvent(ErrorType.INSERT_BEFORE_ERROR, nodeData);
}

function sendAnalyticsEvent(action, nodeData) {
	void getAnalyticsWebClient().then((client) => {
		client.sendOperationalEvent({
			source: 'confluenceFrontendServer',
			actionSubject: 'domManipulationError',
			action: 'error',
			attributes: {
				suppressedError: action,
				...nodeData,
			},
		});
	});
}

function extractNodeData(node) {
	if (!node) return {};

	return {
		nodeName: node.nodeName,
		parentElementNodeName: node.parentElement?.nodeName,
		isConnected: node.isConnected,
	};
}

/**
 * Resets the internal state for testing purposes.
 * @internal
 */
export function _resetForTesting() {
	patchesApplied = false;
}
